import React, { useEffect, useState } from "react";
import { adminApi } from "../../services/adminApi";

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    adminApi.getSettings().then(({ settings: s }) => {
      setSettings(s);
      setForm({
        notificationLeadDays: s.notificationLeadDays ?? 30,
        notificationFrequency: s.notificationFrequency || "daily",
        companyName: s.companyName || "",
        companyFein: s.companyFein || "",
        companyEmail: s.companyEmail || "",
        companyPhone: s.companyPhone || "",
        companyAddress: s.companyAddress || "",
        companyTradeName: s.companyTradeName || "",
        companySosDate: s.companySosDate || "",
        companyNatureBusiness: s.companyNatureBusiness || "Transportation",
        weeklyLeaseAmount: s.weeklyLeaseAmount ?? 0,
        weeklyLeaseNotes: s.weeklyLeaseNotes || "",
        violationPenaltyAmount: s.violationPenaltyAmount ?? 50,
        emailDriverWelcome: s.emailDriverWelcome ?? true,
        emailInsuranceLeaseNotice: s.emailInsuranceLeaseNotice ?? true,
        emailContractEnded: s.emailContractEnded ?? true,
        emailComplianceDigest: s.emailComplianceDigest ?? true,
        pushComplianceAlerts: s.pushComplianceAlerts ?? true,
        emailAdminWelcome: s.emailAdminWelcome ?? true,
      });
    }).catch((err) => setError(err.message));
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await adminApi.updateSettings(form);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  if (!settings && !error) return <div className="flex items-center justify-center h-64 text-gray-400">Loading…</div>;

  const fields = [
    { key: "companyName", label: "Company / Entity Name", placeholder: "Trans Voyage Taxi LLC" },
    { key: "companyTradeName", label: "Registered Trade Name", placeholder: "Trans Voyage Taxi" },
    { key: "companyFein", label: "FEIN / EIN", placeholder: "12-3456789" },
    { key: "companyEmail", label: "Company Email", placeholder: "info@transvoyagetaxi.com" },
    { key: "companyPhone", label: "Company Phone", placeholder: "7205551234" },
    { key: "companyAddress", label: "Mailing Address", placeholder: "123 Main St, Denver CO 80202" },
    { key: "companySosDate", label: "Date of SOS Registration", placeholder: "MM/DD/YYYY" },
    { key: "companyNatureBusiness", label: "Nature of Business", placeholder: "Transportation" },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Settings</h1>
      <p className="text-sm text-gray-500 mb-6">Company info is used to pre-fill WC43 forms. Notification settings control expiry email timing.</p>

      {error && <div className="bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-3 mb-4 text-sm">{error}</div>}
      {saved && <div className="bg-green-50 text-green-700 border border-green-200 rounded-lg px-4 py-3 mb-4 text-sm">Settings saved.</div>}

      <form onSubmit={handleSave} className="space-y-6 max-w-xl">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Compliance Notifications</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notify drivers this many days before expiry
              </label>
              <input
                type="number"
                min={1}
                max={365}
                value={form.notificationLeadDays}
                onChange={(e) => setForm({ ...form, notificationLeadDays: Number(e.target.value) })}
                className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reminder email frequency
              </label>
              <select
                value={form.notificationFrequency}
                onChange={(e) => setForm({ ...form, notificationFrequency: e.target.value })}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="daily">Daily (every day at 8 AM)</option>
                <option value="every_3_days">Every 3 days</option>
                <option value="weekly">Weekly</option>
                <option value="every_2_weeks">Every 2 weeks</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">The system checks daily at 8 AM but only sends emails at this frequency. Emails go from info@transvoyagetaxi.com.</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-1">Weekly Lease Agreement</h2>
          <p className="text-sm text-gray-500 mb-4">This rate is shown to drivers when they acknowledge the lease agreement during enrollment. Changing it takes effect immediately for any driver who has not yet signed.</p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Lease Amount ($)</label>
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-sm">$</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={form.weeklyLeaseAmount}
                  onChange={(e) => setForm({ ...form, weeklyLeaseAmount: parseFloat(e.target.value) || 0 })}
                  className="w-36 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                />
                <span className="text-sm text-gray-400">per week</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Terms / Notes (optional)</label>
              <textarea
                value={form.weeklyLeaseNotes}
                onChange={(e) => setForm({ ...form, weeklyLeaseNotes: e.target.value })}
                rows={3}
                placeholder="e.g. Rate includes dispatch fee. Payment due every Monday by 11:59 PM."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-1">Missing Trip / DIA Gate Fee Violation Fee (§16)</h2>
          <p className="text-sm text-gray-500 mb-4">
            Per Section 16 of the contractor agreement, drivers are charged this fee for each trip found in DIA gate-fee or airport access records that is missing from the Company-approved app. Drivers must sign a separate acknowledgment of this policy during enrollment.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Missing Trip Violation Fee ($)</label>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-sm">$</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={form.violationPenaltyAmount}
                onChange={(e) => setForm({ ...form, violationPenaltyAmount: parseFloat(e.target.value) || 0 })}
                className="w-36 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="50.00"
              />
              <span className="text-sm text-gray-400">per missing app-registered trip</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">This amount is shown to the driver in their enrollment acknowledgment and is embedded in the signed PDF stored in Dropbox.</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-1">Email Notifications</h2>
          <p className="text-sm text-gray-500 mb-4">
            Disable specific emails to suppress them — useful when onboarding existing drivers who don't need welcome messages or lease notices.
          </p>
          <div className="space-y-3">
            {[
              { key: "emailDriverWelcome", label: "New driver welcome email", desc: "Sent when a driver account is created — includes login credentials and enrollment instructions." },
              { key: "emailInsuranceLeaseNotice", label: "Insurance card & lease notice", desc: "Sent when an insurance card is uploaded — summarises the weekly lease rate and key terms." },
              { key: "emailContractEnded", label: "Contract termination notice", desc: "Sent when a driver is set to Inactive — notifies them that their contractor relationship has ended." },
              { key: "emailComplianceDigest", label: "Compliance expiry reminders", desc: "Sent on the configured schedule when compliance documents, licenses, or insurance are expiring." },
              { key: "pushComplianceAlerts", label: "FCM & realtime compliance reminders", desc: "Sends driver app push and Socket.IO alerts on the configured compliance reminder schedule." },
              { key: "emailAdminWelcome", label: "Admin account welcome email", desc: "Sent when a new admin user is created — includes their login credentials." },
            ].map(({ key, label, desc }) => (
              <label key={key} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${form[key] ? "border-blue-200 bg-blue-50" : "border-gray-200 bg-gray-50"}`}>
                <div className="mt-0.5 flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={!!form[key]}
                    onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                    className="accent-blue-600 w-4 h-4"
                  />
                </div>
                <div>
                  <p className={`text-sm font-medium ${form[key] ? "text-blue-900" : "text-gray-500"}`}>{label}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Company Information (WC43 Pre-fill)</h2>
          <div className="space-y-3">
            {fields.map(({ key, label, placeholder }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                <input
                  value={form[key] || ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={placeholder}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ))}
          </div>
        </div>

        <button type="submit" disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition disabled:opacity-60">
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </form>
    </div>
  );
}
